import { useEffect, useState } from 'react';
import type { ChatState } from '../types';
import Header from '../components/Header';
import MessageList from '../components/MessageList';
import LoadingIndicator from '../components/LoadingIndicator';
import PromptInput from '../components/PromptInput';
import ModelSelector from '../components/ModelSelector';
import {
  fetchModels,
  postChat,
  UnauthorizedError,
  RequestTimeoutError,
  UpstreamError,
  BadRequestError,
} from '../api';

interface ChatPageProps {
  accessToken: string;
  onSignOut: () => void;
}

export default function ChatPage({ accessToken, onSignOut }: ChatPageProps) {
  const [chatState, setChatState] = useState<ChatState>({
    messages: [],
    isLoading: false,
    error: null,
    models: [],
    selectedModelId: null,
    catalogError: null,
  });

  // Fetch the model catalog on mount. On failure, record the error so the
  // ModelSelector can surface it and the PromptInput submit button is disabled.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const models = await fetchModels(accessToken);
        if (cancelled) return;
        setChatState((prev) => ({
          ...prev,
          models,
          selectedModelId: models[0]?.modelId ?? null,
          catalogError: null,
        }));
      } catch (err: unknown) {
        if (cancelled) return;
        if (err instanceof UnauthorizedError) {
          onSignOut();
          return;
        }
        const message =
          err instanceof Error ? err.message : 'Failed to load model catalog';
        setChatState((prev) => ({ ...prev, catalogError: message }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accessToken, onSignOut]);

  const handleSubmit = async (prompt: string) => {
    if (!chatState.selectedModelId) return;
    const modelId = chatState.selectedModelId;

    const userMessage = {
      id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      role: 'user' as const,
      content: prompt,
      timestamp: Date.now(),
    };

    setChatState((prev) => ({
      ...prev,
      messages: [...prev.messages, userMessage],
      isLoading: true,
      error: null,
    }));

    try {
      const data = await postChat(accessToken, prompt, modelId);

      const assistantMessage = {
        id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        role: 'assistant' as const,
        content: data.response,
        timestamp: Date.now(),
        metrics: data.metrics,
      };

      setChatState((prev) => ({
        ...prev,
        messages: [...prev.messages, assistantMessage],
        isLoading: false,
        error: null,
      }));
    } catch (err: unknown) {
      if (err instanceof UnauthorizedError) {
        onSignOut();
        return;
      }

      let errorMessage: string;
      if (err instanceof RequestTimeoutError) {
        errorMessage = 'Request timed out';
      } else if (err instanceof UpstreamError) {
        errorMessage = err.message;
      } else if (err instanceof BadRequestError) {
        errorMessage = err.message;
      } else {
        errorMessage = 'Unable to connect. Please try again.';
      }

      setChatState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
    }
  };

  // Submit is disabled while a request is in flight OR when the catalog
  // failed to load (no valid modelId to send).
  const promptInputDisabled =
    chatState.isLoading || chatState.catalogError !== null;

  return (
    <div className="chat-page">
      <Header onSignOut={onSignOut} />
      <MessageList
        messages={chatState.messages}
        error={chatState.error}
        models={chatState.models}
      />
      {chatState.isLoading && <LoadingIndicator />}
      <div className="chat-input-area">
        <ModelSelector
          models={chatState.models}
          selectedModelId={chatState.selectedModelId}
          onChange={(id) =>
            setChatState((prev) => ({ ...prev, selectedModelId: id }))
          }
          disabled={chatState.isLoading}
          error={chatState.catalogError}
        />
        <PromptInput onSubmit={handleSubmit} isLoading={promptInputDisabled} />
      </div>
    </div>
  );
}
